import { useEffect, useRef } from 'react';

interface Heart {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  speed: number;
  drift: number;
  driftAngle: number;
  rotation: number;
  rotationSpeed: number;
  delay: number;
}

export default function FloatingHearts() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heartsRef = useRef<Heart[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const HEART_COUNT = 38;

    const createHeart = (id: number, immediate = false): Heart => ({
      id,
      x: Math.random() * window.innerWidth,
      y: immediate ? Math.random() * window.innerHeight : window.innerHeight + Math.random() * 200,
      size: 20 + Math.random() * 45,
      opacity: 0.08 + Math.random() * 0.22,
      speed: 0.3 + Math.random() * 0.7,
      drift: 0.3 + Math.random() * 0.6,
      driftAngle: Math.random() * Math.PI * 2,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.015,
      delay: immediate ? 0 : Math.random() * 200,
    });

    heartsRef.current = Array.from({ length: HEART_COUNT }, (_, i) =>
      createHeart(i, true)
    );

    const drawHeart = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
      ctx.save();
      ctx.beginPath();
      const s = size / 10;
      ctx.moveTo(x, y + s * 3);
      ctx.bezierCurveTo(x, y + s * 1, x - s * 4, y - s * 1.5, x - s * 4, y + s * 1);
      ctx.bezierCurveTo(x - s * 4, y + s * 3.5, x, y + s * 6, x, y + s * 8);
      ctx.bezierCurveTo(x, y + s * 6, x + s * 4, y + s * 3.5, x + s * 4, y + s * 1);
      ctx.bezierCurveTo(x + s * 4, y - s * 1.5, x, y + s * 1, x, y + s * 3);
      ctx.closePath();
      ctx.restore();
    };

    let frameCount = 0;

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      frameCount++;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      heartsRef.current = heartsRef.current.map((heart) => {
        if (heart.delay > 0) {
          return { ...heart, delay: heart.delay - 1 };
        }

        const newY = heart.y - heart.speed;
        const newX = heart.x + Math.sin(heart.driftAngle) * heart.drift;
        const newDriftAngle = heart.driftAngle + 0.018;
        const newRotation = heart.rotation + heart.rotationSpeed;

        // Draw
        ctx.save();
        ctx.translate(newX, newY);
        ctx.rotate(newRotation);

        // Pick pinkish/red hues
        const hues = [350, 340, 355, 0, 330, 345];
        const hue = hues[heart.id % hues.length];
        const sat = 70 + (heart.id % 3) * 10;
        const lit = 65 + (heart.id % 4) * 5;

        const gradient = ctx.createRadialGradient(0, heart.size * 0.3, 0, 0, heart.size * 0.3, heart.size * 0.8);
        gradient.addColorStop(0, `hsla(${hue}, ${sat}%, ${lit + 15}%, ${heart.opacity * 1.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${sat}%, ${lit}%, ${heart.opacity * 0.5})`);

        ctx.fillStyle = gradient;

        drawHeart(ctx, 0, -heart.size * 0.5, heart.size);
        ctx.fill();
        ctx.restore();

        if (newY < -heart.size * 2) {
          return createHeart(heart.id);
        }

        return {
          ...heart,
          y: newY,
          x: newX,
          driftAngle: newDriftAngle,
          rotation: newRotation,
        };
      });
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
}
